import classnames from 'classnames';
import React, { useState, useRef, useEffect } from 'react';
import FocusLock, { AutoFocusInside } from 'react-focus-lock';
import { useLayer, Arrow } from 'react-laag';
import { Transition } from 'react-transition-group';
import { Placements } from '../../../../enums/placements.js';
import { PopoutVariants } from '../../../../enums/popout.js';
import { useClickOutside } from '../../../../hooks/use-click-outside.js';
import '../../../../utilities/color.js';
import { getContainer } from '../../../../utilities/container.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import { mergeRefs } from '../../../../utilities/refs.js';
import { useHover } from '../../../../hooks/use-hover.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import 'styled-components';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../styles/styled-components/input.js';
import '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import { transitionFadeIn } from '../../../../styles/transitions/fade-in.js';
import '@brandfolder/utilities';
import { CloseButton } from '../../button/close-button/close-button.js';
import '../../button/color-button/color-button.js';
import '../../tooltip/hover-tooltip/hover-tooltip.js';
import '../../icon/font-icon/font-icon.js';
import '../../button/icon-button/icon-button.js';
import '../../button/standard-button/standard-button.js';
import { CircleLoader } from '../../loader/circle-loader/circle-loader.js';
import '../../loader/shimmer-loader/shimmer-loader.js';

const BasePopout = (props)=>{
    const { arrowSvgProps, autoFocus = true, children, closeButtonLabel, closeButtonProps, closeOnClickAway = false, container, containerOffset = 10, content, elementToFocus, focusLock = true, focusLockProps, footer, height, id, insidePopout, labels, loading, onClose, open, openOnHover, placement = Placements.TopCenter, popoutDialogBodyProps, popoutDialogFooterProps, popoutDialogHeaderProps, popoutDialogHeadingProps, popoutDialogInnerProps, popoutDialogProps, popoutDialogTitleProps, preventCutoff = true, setOpen, showArrow = true, showCloseButton = true, title, titleAfter, titleBefore, transitionDelay = 0, transitionDuration = 200, triggerOffset = 8, triggerWrapperProps, variant = PopoutVariants.None, width = 300, ...otherProps } = props;
    const ariaLabelledBy = title ? `${id}-label` : undefined;
    const popoutId = `${id}-popout`;
    const timeout = transitionDelay ? transitionDelay + transitionDuration : transitionDuration;
    const [hoveredDelayed, setHoveredDelayed] = useState(false);
    const wrapperRef = useRef(null);
    const { clickedOutside } = useClickOutside({
        ref: wrapperRef
    });
    const nodeRef = useRef(null);
    const { hoverProps, hovered, setHovered } = useHover();
    const handleOnClose = ()=>{
        if (onClose) onClose();
    };
    const handleClose = ()=>{
        handleOnClose();
        setHoveredDelayed(false);
        setHovered(false);
        setOpen(false);
    };
    const onOutsideClick = ()=>{
        if (closeOnClickAway) {
            handleClose();
        }
    };
    const isOpen = open || hoveredDelayed;
    const { arrowProps, layerProps, renderLayer, triggerProps } = useLayer({
        auto: preventCutoff,
        container: container || getContainer(popoutId),
        containerOffset,
        isOpen,
        placement,
        onOutsideClick,
        triggerOffset
    });
    const { ref, style: layerPropsStyle, ...otherLayerProps } = layerProps;
    useEffect(()=>{
        if (openOnHover) {
            if (hovered) {
                setHoveredDelayed(true);
            } else {
                setTimeout(()=>{
                    setHoveredDelayed(false);
                }, timeout);
            }
        }
    }, [
        hovered,
        openOnHover
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (elementToFocus?.current) {
            elementToFocus.current.focus();
        }
    }, [
        elementToFocus
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(()=>{
        if (insidePopout && clickedOutside && closeOnClickAway) {
            handleClose();
        }
    }, [
        clickedOutside
    ]); // eslint-disable-line react-hooks/exhaustive-deps
    const showClose = open && showCloseButton;
    const renderHeader = ()=>{
        return /*#__PURE__*/ React.createElement("header", {
            ...popoutDialogHeaderProps,
            className: classnames({
                'bf-popout__header': true,
                [`${popoutDialogHeaderProps?.className}`]: !!popoutDialogHeaderProps?.className
            })
        }, titleBefore, title && /*#__PURE__*/ React.createElement("div", {
            ...popoutDialogTitleProps,
            className: classnames({
                'bf-popout__title': true,
                [`${popoutDialogTitleProps?.className}`]: !!popoutDialogTitleProps?.className
            })
        }, /*#__PURE__*/ React.createElement("h6", {
            ...popoutDialogHeadingProps,
            className: classnames({
                'bf-popout__heading': true,
                [`${popoutDialogHeadingProps?.className}`]: !!popoutDialogHeadingProps?.className
            }),
            id: ariaLabelledBy
        }, title)), titleAfter, /*#__PURE__*/ React.createElement(CloseButton, {
            ...closeButtonProps,
            "aria-label": labels?.closeButtonLabel || closeButtonLabel || '',
            className: classnames({
                'bf-popout__close': true,
                'bf-popout__close--hidden': !showClose,
                [`${closeButtonProps?.className}`]: !!closeButtonProps?.className
            }),
            onClick: ()=>handleClose()
        }));
    };
    const renderContent = ()=>{
        return /*#__PURE__*/ React.createElement(React.Fragment, null, !loading && content, labels?.loaderLabel && loading && /*#__PURE__*/ React.createElement(CircleLoader, {
            label: labels?.loaderLabel
        }));
    };
    const renderInner = ()=>{
        return /*#__PURE__*/ React.createElement(React.Fragment, null, renderHeader(), /*#__PURE__*/ React.createElement("div", {
            ...popoutDialogBodyProps,
            className: classnames({
                'bf-popout__body': true,
                [`${popoutDialogBodyProps?.className}`]: !!popoutDialogBodyProps?.className
            })
        }, autoFocus ? /*#__PURE__*/ React.createElement(AutoFocusInside, {
            className: "bf-popout__autofocus"
        }, renderContent()) : renderContent()), footer && /*#__PURE__*/ React.createElement("footer", {
            ...popoutDialogFooterProps,
            className: classnames({
                'bf-popout__footer': true,
                [`${popoutDialogFooterProps?.className}`]: !!popoutDialogFooterProps?.className
            })
        }, footer));
    };
    return /*#__PURE__*/ React.createElement("div", {
        ...otherProps,
        ...hoverProps,
        className: classnames({
            'bf-popout': true,
            'bf-popout--hovered': hoveredDelayed,
            'bf-popout--loading': loading,
            'bf-popout--open': isOpen,
            [`bf-popout__${variant}`]: variant !== PopoutVariants.None,
            [`${otherProps?.className}`]: !!otherProps?.className
        }),
        id: popoutId,
        ref: wrapperRef
    }, /*#__PURE__*/ React.createElement("span", {
        ...triggerWrapperProps,
        ...triggerProps,
        className: classnames({
            'bf-popout__trigger': true,
            [`${triggerWrapperProps?.className}`]: !!triggerWrapperProps?.className
        })
    }, children), renderLayer(/*#__PURE__*/ React.createElement(Transition, {
        appear: isOpen,
        in: isOpen,
        mountOnEnter: true,
        nodeRef: nodeRef,
        timeout: timeout,
        unmountOnExit: true
    }, (transitionStatus)=>/*#__PURE__*/ React.createElement("div", {
            ...popoutDialogProps,
            ...otherLayerProps,
            "aria-labelledby": ariaLabelledBy,
            className: classnames({
                'bf-popout__dialog': true,
                [`bf-popout__dialog--has-arrow`]: showArrow,
                [`${popoutDialogProps?.className}`]: !!popoutDialogProps?.className
            }),
            id: id,
            ref: mergeRefs([
                nodeRef,
                ref
            ]),
            role: "dialog",
            style: {
                ...popoutDialogProps?.style,
                ...layerPropsStyle,
                height,
                width,
                ...transitionFadeIn({
                    transitionDelay,
                    transitionDuration,
                    transitionStatus
                })
            }
        }, /*#__PURE__*/ React.createElement("div", {
            ...popoutDialogInnerProps,
            className: classnames({
                'bf-popout__dialog-inner': true,
                [`bf-popout__dialog-inner--${placement}`]: placement,
                [`bf-popout__dialog-inner--has-close-button`]: showClose,
                [`bf-popout__dialog-inner--has-footer`]: !!footer,
                [`bf-popout__dialog-inner--has-title`]: !!title,
                [`${popoutDialogInnerProps?.className}`]: !!popoutDialogInnerProps?.className
            }),
            style: {
                ...popoutDialogInnerProps?.style,
                height,
                width
            }
        }, focusLock ? /*#__PURE__*/ React.createElement(FocusLock, {
            ...focusLockProps,
            className: classnames({
                'bf-popout__focus-lock': true,
                [`${focusLockProps?.className}`]: !!focusLockProps?.className
            }),
            crossFrame: focusLockProps?.crossFrame || false
        }, renderInner()) : renderInner()), showArrow && /*#__PURE__*/ React.createElement(Arrow, {
            onPointerEnterCapture: undefined,
            onPointerLeaveCapture: undefined,
            ...arrowSvgProps,
            ...arrowProps,
            className: classnames({
                'bf-popout__arrow': true,
                [`bf-popout__arrow--${placement}`]: placement
            })
        })))));
};

export { BasePopout };
//# sourceMappingURL=base-popout.js.map
